import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form as FormikForm } from "formik";
import { Button, Row, Col } from "react-bootstrap";
import OtpInput from "react-otp-input";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { post } from "../../../helpers/api_helper";

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(location.state?.userData || {});
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(true);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timer, setTimer] = useState(15);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    let interval;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setIsTimerActive(false);
            return 15;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  const otpSchema = Yup.object().shape({
    otp: Yup.string()
      .required("OTP is required")
      .length(4, "OTP must be 4 digits"),
  });

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const response = await post("/user/verify/email", {
        email: userData.email,
        otp: values.otp,
      });
      if (response.success) {
        toast.success("OTP verified successfully");
        navigate("/login");
      } else {
        setStatus({ server: response?.message || "Invalid OTP" });
        toast.error(response?.message || "Invalid OTP");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error during OTP verification";
      setStatus({ server: errorMessage });
      toast.error(errorMessage);
      console.error("Error during OTP verification:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (attempts < 2) {
      setAttempts(attempts + 1);
      setIsTimerActive(true);
      try {
        await post("/user/register", { ...userData });
        toast.success("OTP resent successfully");
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error during OTP resend";
        toast.error(errorMessage);
        console.error("Error during OTP resend:", error);
      }
    } else {
      toast.error("Maximum resend attempts reached. Please sign up again.");
      navigate("/signup");
    }
  };

  const handleVerifyOtp = async () => {
    handleSubmit({ otp }, { setSubmitting: () => {}, setStatus: () => {} });
  };

  return (
    <div className="formInner">
      <div className="top py-2 text-center">
        <h2 className="m-0 fw-sbold head">OTP Verification</h2>
        <p className="m-0 py-1">Please enter the OTP sent to your email</p>
      </div>
      <Formik
        initialValues={{ otp: "" }}
        validationSchema={otpSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, status }) => (
          <FormikForm>
            <Row>
              {otpSent && (
                <>
                  <Col lg="12" className="my-3">
                    <div className="FormHeading">
                      <h4 className="m-0 fw-sbold themeClr">
                        Verification Code
                      </h4>
                      <p className="m-0">
                        Please type the verification code sent to{" "}
                        <span className="themeClr">{userData.email}</span>
                      </p>
                    </div>
                  </Col>
                  <Col lg="12" className="my-3">
                    <div className="otp mb-3">
                      <OtpInput
                        value={otp}
                        onChange={setOtp}
                        numInputs={4}
                        renderInput={(props) => <input {...props} />}
                      />
                    </div>
                    <p className="text-muted fw-sbold m-0">
                      I don’t receive a code!{" "}
                      {attempts >= 2 ? (
                        <span
                          className="themeClr fw-sbold"
                          role="button"
                          onClick={() => navigate("/signup")}
                        >
                          Try again
                        </span>
                      ) : (
                        <Button
                          className="p-0 border-0 themeClr fw-sbold"
                          variant="transparent"
                          onClick={handleResendOtp}
                          disabled={isTimerActive}
                          style={{
                            color: isTimerActive ? "lightgray" : "blue",
                            pointerEvents: isTimerActive ? "none" : "auto",
                          }}
                        >
                          {isTimerActive
                            ? `Please Resend in ${timer}s`
                            : "Please Resend"}
                        </Button>
                      )}
                    </p>
                  </Col>
                  <Col lg="12" className="my-3">
                    <Button
                      onClick={handleVerifyOtp}
                      className="d-flex align-items-center justify-content-center w-100 commonBtn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Loading..." : "VERIFY OTP"}
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default OtpVerification;
